import { z } from 'zod';
import { defineTool, CLINICAL_STAFF, STAFF, PRESCRIBERS } from './define.js';
import { IsoDate } from '../types/common.js';
import { ClinicalNoteSchema, ClinicalOrderSchema, EncounterSchema, EncounterTypeSchema, LabResultSchema, MedicationCatalogItemSchema, MedicationSchema, OrderItemSchema, PrescriptionSchema, RadiologyReportSchema, DosageSchema } from '../types/clinical.js';
import { PendingActionSchema } from '../types/session.js';
import { CdsEvaluationSchema, CdsFindingSchema, CdsHookSchema } from '../cds.js';

const STAFF_AND_PATIENT = [...STAFF, 'PATIENT'] as const;

// ---------------------------------------------------------------- Encounters
export const createEncounter = defineTool({
  name: 'createEncounter',
  description: 'Start an encounter for the active patient and make it the active encounter. Required before clinical writes such as notes and orders. Optionally link an appointment.',
  input: z.object({ type: EncounterTypeSchema.default('OFFICE_VISIT'), appointmentId: z.string().optional(), chiefComplaint: z.string().max(500).optional() }),
  output: z.object({ encounter: EncounterSchema }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'ENCOUNTER_CREATE',
});

export const getEncounters = defineTool({
  name: 'getEncounters',
  description: 'Previous encounters for the active patient, newest first.',
  input: z.object({ limit: z.number().int().min(1).max(50).default(10) }),
  output: z.object({ encounters: z.array(EncounterSchema) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const updateEncounterDraft = defineTool({
  name: 'updateEncounterDraft',
  description: 'Prepare documentation on the active encounter (chief complaint, HPI, review of systems, exam, assessment, plan, follow-up instructions) from an explicit clinician statement. Returns a pending action for review before saving.',
  input: z.object({
    fields: EncounterSchema.pick({ chiefComplaint: true, historyOfPresentIllness: true, reviewOfSystems: true, physicalExam: true, assessment: true, plan: true, followUpInstructions: true }).partial(),
    mode: z.enum(['replace', 'append']).default('append'),
  }),
  output: z.object({ pendingAction: PendingActionSchema }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'ENCOUNTER_UPDATE',
});

export const completeEncounter = defineTool({
  name: 'completeEncounter',
  description: 'Complete the active encounter. Fails if there are unresolved pending actions for it.',
  input: z.object({ encounterId: z.string().optional() }),
  output: z.object({ encounter: EncounterSchema }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'ENCOUNTER_COMPLETE',
});

// ---------------------------------------------------------------- Results
export const getLabResults = defineTool({
  name: 'getLabResults',
  description: 'Laboratory results for the active patient. Filter by LOINC/local code(s) and date range. Returns verified observations with units, reference ranges and abnormal flags. Returns an empty list when there are none; never estimates.',
  input: z.object({
    codes: z.array(z.string()).optional().describe('Test codes or well-known names like HBA1C, CBC, LIPID'),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    latestOnly: z.boolean().default(false).describe('Only the most recent value per test'),
    limit: z.number().int().min(1).max(500).default(100),
  }),
  output: z.object({ results: z.array(LabResultSchema), resolvedCodes: z.array(z.string()) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const getLabTrends = defineTool({
  name: 'getLabTrends',
  description: 'Time-series of one or more lab tests for charting (e.g. HbA1c over six months). Returns ordered points with units and reference range.',
  input: z.object({
    codes: z.array(z.string()).min(1),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
    months: z.number().int().min(1).max(120).optional().describe('Shortcut for from = today - months'),
  }),
  output: z.object({
    series: z.array(z.object({
      code: z.string(), display: z.string(), unit: z.string().optional(),
      referenceRange: z.object({ low: z.number().optional(), high: z.number().optional() }).optional(),
      points: z.array(z.object({ at: z.string(), value: z.number(), flag: z.string(), resultId: z.string() })),
    })),
    from: IsoDate, to: IsoDate,
  }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const getRadiologyReports = defineTool({
  name: 'getRadiologyReports',
  description: 'Radiology/imaging reports for the active patient, newest first.',
  input: z.object({ modality: z.string().optional(), limit: z.number().int().min(1).max(50).default(10) }),
  output: z.object({ reports: z.array(RadiologyReportSchema) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

// ---------------------------------------------------------------- Medications
export const getMedicationList = defineTool({
  name: 'getMedicationList',
  description: 'Current and past medications on the active patient medication list, with provenance (clinician-entered vs patient-reported).',
  input: z.object({ includeInactive: z.boolean().default(false) }),
  output: z.object({ medications: z.array(MedicationSchema) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const searchMedicationCatalog = defineTool({
  name: 'searchMedicationCatalog',
  description: 'Search the medication catalog by name or ingredient. Returns candidates with their standard strengths and routes; the catalog does not supply a dose, the clinician must.',
  input: z.object({ query: z.string().min(1).max(80), limit: z.number().int().min(1).max(20).default(8) }),
  output: z.object({ items: z.array(MedicationCatalogItemSchema) }),
  roles: STAFF, requiresPatientContext: false, write: false, exposeToLlm: true,
});

export const createPrescriptionDraft = defineTool({
  name: 'createPrescriptionDraft',
  description: 'Draft a prescription for the active patient. Dose, route and frequency MUST be stated by the clinician; the tool rejects missing fields instead of guessing. CDS runs on the draft and findings are returned with the pending action.',
  input: z.object({
    medicationCode: z.string().optional().describe('From searchMedicationCatalog when matched'),
    medicationName: z.string().min(1),
    dosage: DosageSchema,
  }),
  output: z.object({ pendingAction: PendingActionSchema, prescription: PrescriptionSchema, cds: CdsEvaluationSchema }),
  roles: PRESCRIBERS, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'PRESCRIPTION_DRAFT',
});

export const confirmPrescription = defineTool({
  name: 'confirmPrescription',
  description: 'Confirm a pending PRESCRIPTION. Critical CDS findings must be listed in acknowledgedFindingIds. Idempotent.',
  input: z.object({ pendingActionId: z.string(), acknowledgedFindingIds: z.array(z.string()).default([]) }),
  output: z.object({ prescription: PrescriptionSchema, replayed: z.boolean() }),
  roles: PRESCRIBERS, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'PRESCRIPTION_CONFIRM',
});

// ---------------------------------------------------------------- Orders
export const createOrderDraft = defineTool({
  name: 'createOrderDraft',
  description: 'Draft laboratory or radiology orders for the active patient from named tests (e.g. "CBC and chest X-ray"). Unknown test names are returned as unresolved for clarification rather than guessed. CDS (duplicates, etc.) runs on the draft.',
  input: z.object({
    category: z.enum(['LAB', 'RADIOLOGY']),
    items: z.array(z.object({
      name: z.string().min(1).describe('Test name as spoken, e.g. CBC, HbA1c, chest X-ray'),
      priority: z.enum(['ROUTINE', 'URGENT', 'STAT']).default('ROUTINE'),
    })).min(1),
    clinicalIndication: z.string().max(300).optional(),
  }),
  output: z.object({
    pendingAction: PendingActionSchema.optional(),
    order: ClinicalOrderSchema.optional(),
    resolvedItems: z.array(OrderItemSchema),
    unresolved: z.array(z.object({ name: z.string(), suggestions: z.array(OrderItemSchema) })),
    cds: CdsEvaluationSchema.optional(),
  }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'ORDER_DRAFT',
});

export const confirmOrder = defineTool({
  name: 'confirmOrder',
  description: 'Confirm a pending ORDER. Idempotent.',
  input: z.object({ pendingActionId: z.string(), acknowledgedFindingIds: z.array(z.string()).default([]) }),
  output: z.object({ order: ClinicalOrderSchema, replayed: z.boolean() }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'ORDER_CONFIRM',
});

export const getOrders = defineTool({
  name: 'getOrders',
  description: 'Orders for the active patient, newest first.',
  input: z.object({ category: z.enum(['LAB', 'RADIOLOGY']).optional(), status: z.array(z.string()).optional(), limit: z.number().int().min(1).max(100).default(20) }),
  output: z.object({ orders: z.array(ClinicalOrderSchema) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

// ---------------------------------------------------------------- Notes
export const getClinicalNotes = defineTool({
  name: 'getClinicalNotes',
  description: 'Clinical notes for the active patient, newest first.',
  input: z.object({ encounterId: z.string().optional(), limit: z.number().int().min(1).max(50).default(10) }),
  output: z.object({ notes: z.array(ClinicalNoteSchema) }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'RECORD_VIEW',
});

export const createClinicalNoteDraft = defineTool({
  name: 'createClinicalNoteDraft',
  description: 'Create a note draft on the active encounter from the clinician\'s explicit words. The body must be the clinician\'s content; do not add clinical facts that were not stated. Returns a pending action to review and save.',
  input: z.object({
    type: ClinicalNoteSchema.shape.type.default('PROGRESS'),
    title: z.string().max(120).optional(),
    body: z.string().min(1).max(20000),
  }),
  output: z.object({ pendingAction: PendingActionSchema, note: ClinicalNoteSchema }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'NOTE_DRAFT',
});

export const saveClinicalNote = defineTool({
  name: 'saveClinicalNote',
  description: 'Confirm a pending CLINICAL_NOTE, persisting it as a DRAFT note (sign separately). Idempotent.',
  input: z.object({ pendingActionId: z.string(), sign: z.boolean().default(false) }),
  output: z.object({ note: ClinicalNoteSchema, replayed: z.boolean() }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'NOTE_SAVE',
});

export const signClinicalNote = defineTool({
  name: 'signClinicalNote',
  description: 'Sign a saved note authored by the caller.',
  input: z.object({ noteId: z.string() }),
  output: z.object({ note: ClinicalNoteSchema }),
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'NOTE_SIGN',
});

// ---------------------------------------------------------------- CDS
export const evaluateClinicalDecisionSupport = defineTool({
  name: 'evaluateClinicalDecisionSupport',
  description: 'Run the deterministic CDS engine for the active patient on a hook (patient-view screening reminders, or a draft order/prescription). The engine, not the model, produces findings; report them verbatim.',
  input: z.object({
    hook: CdsHookSchema,
    draftOrderItems: z.array(OrderItemSchema).optional(),
    draftPrescription: z.object({ medicationName: z.string(), ingredient: z.string().optional(), classes: z.array(z.string()).optional(), dosage: DosageSchema.partial().optional() }).optional(),
  }),
  output: CdsEvaluationSchema,
  roles: CLINICAL_STAFF, requiresPatientContext: true, write: false, exposeToLlm: true, audit: 'CDS_EVALUATE',
});

export const _findingSchema = CdsFindingSchema;
